import { useEffect, useMemo, useState } from "react";
import type { QaAgentSummary, QaDrillResponse, QaMonthsResponse, QaSummary } from "@tienda/shared";
import { displayAgentName } from "@tienda/shared";
import { fetchDrill, fetchMonths, fetchSummary } from "./api";
import corefoneLogo from "./assets/corefone-logo-green.svg";

function formatMonth(month: string): string {
  const [year, monthNumber] = month.split("-").map(Number);
  if (!year || !monthNumber) return month;
  return new Intl.DateTimeFormat("es-UY", { month: "long", year: "numeric" }).format(
    new Date(year, monthNumber - 1, 15, 12)
  );
}

function formatScore(score: number | null): string {
  return score === null ? "—" : `${score.toFixed(1)}%`;
}

function formatCount(value: number): string {
  return new Intl.NumberFormat("es-UY").format(value);
}

function scoreClass(score: number | null): string {
  if (score === null) return "score score--empty";
  if (score < 60) return "score score--critical";
  if (score < 80) return "score score--watch";
  return "score score--good";
}

function incidenceRate(agent: QaAgentSummary | null): number | null {
  if (!agent || !agent.evaluations) return null;
  return Number(((agent.failed_evaluations / agent.evaluations) * 100).toFixed(1));
}

function qualityCellClass(score: number | null): string {
  if (score === null) return "qa-table__metric qa-table__metric--empty";
  if (score < 60) return "qa-table__metric qa-table__metric--critical";
  if (score < 80) return "qa-table__metric qa-table__metric--watch";
  return "qa-table__metric qa-table__metric--good";
}

function errorCellClass(rate: number | null): string {
  if (rate === null) return "qa-table__metric qa-error-rate qa-table__metric--empty";
  if (rate > 20) return "qa-table__metric qa-error-rate qa-table__metric--critical";
  if (rate > 10) return "qa-table__metric qa-error-rate qa-table__metric--watch";
  return "qa-table__metric qa-error-rate qa-table__metric--good";
}

function criterionPassRate(failureRate: number | null): number | null {
  return failureRate === null ? null : Number((100 - failureRate).toFixed(1));
}

function selectCriteriaByFailureRate(
  criteria: QaSummary["criteria"],
  direction: "asc" | "desc",
  predicate: (criterion: QaSummary["criteria"][number]) => boolean
) {
  return criteria
    .filter((criterion) => criterion.failure_rate !== null)
    .filter(predicate)
    .sort((left, right) => {
      const leftRate = left.failure_rate ?? 0;
      const rightRate = right.failure_rate ?? 0;
      const comparison = direction === "asc" ? leftRate - rightRate : rightRate - leftRate;
      return comparison || right.evaluated_answers - left.evaluated_answers || left.criterion_label.localeCompare(right.criterion_label);
    })
    .slice(0, 3);
}

function InsightList({
  criteria,
  kind,
  emptyMessage
}: {
  criteria: QaSummary["criteria"];
  kind: "strength" | "attention" | "rare";
  emptyMessage: string;
}) {
  if (!criteria.length) return <p className="agent-summary__empty">{emptyMessage}</p>;

  return (
    <div className="agent-summary__list">
      {criteria.map((criterion) => {
        const passRate = criterionPassRate(criterion.failure_rate);
        return (
          <article className={`agent-insight agent-insight--${kind}`} key={criterion.criterion_key}>
            <div className="agent-insight__heading">
              <strong>{criterion.criterion_label}</strong>
              <span>{formatScore(passRate)}</span>
            </div>
            <div className="agent-insight__bar" aria-hidden="true">
              <span style={{ width: `${Math.max(0, Math.min(100, passRate ?? 0))}%` }} />
            </div>
            <small>
              {formatCount(criterion.passed_answers)} aprobadas · {formatCount(criterion.failed_answers)} fallas · {formatCount(criterion.evaluated_answers)} respuestas evaluadas
            </small>
          </article>
        );
      })}
    </div>
  );
}

function ClearAgentButton({ agent, onClear }: { agent: string; onClear: () => void }) {
  return (
    <button
      className="agent-summary__clear"
      type="button"
      onClick={onClear}
      aria-label={`Limpiar agente ${displayAgentName(agent)}`}
    >
      × Limpiar selección
    </button>
  );
}

function AgentPerformanceSummary({ agent, summary, onClear }: { agent: string; summary: QaSummary; onClear: () => void }) {
  const eligibleCriteria = summary.criteria.filter((criterion) => criterion.failure_rate !== null);
  const strengths = selectCriteriaByFailureRate(summary.criteria, "asc", (criterion) => criterion.failure_rate === 0);
  const attention = selectCriteriaByFailureRate(summary.criteria, "desc", (criterion) => (criterion.failure_rate ?? 0) >= 20);
  const rareDeviations = selectCriteriaByFailureRate(
    summary.criteria,
    "desc",
    (criterion) => (criterion.failure_rate ?? 0) > 0 && (criterion.failure_rate ?? 0) < 20
  );
  const strengthNames = strengths.slice(0, 2).map((criterion) => criterion.criterion_label).join(" e ");
  const attentionNames = attention.slice(0, 2).map((criterion) => criterion.criterion_label).join(" e ");
  const rareDeviationNames = rareDeviations.slice(0, 2).map((criterion) => criterion.criterion_label).join(" e ");

  let narrative = `Resumen general de ${formatCount(summary.totals.evaluations)} monitorias disponibles: score QA promedio ${formatScore(summary.totals.avg_score)} y ${formatScore(summary.totals.clean_rate)} sin incidencias. Se revisaron ${formatCount(eligibleCriteria.length)} criterios evaluables del período.`;
  if (!eligibleCriteria.length) {
    narrative += " No hay criterios con respuestas evaluables en este período.";
  } else {
    if (attentionNames) narrative += ` Más fallas en ${attentionNames}.`;
    if (strengthNames) narrative += ` Buen desempeño en ${strengthNames}.`;
    if (rareDeviationNames) narrative += ` Desvíos raros en ${rareDeviationNames}.`;
    if (!attentionNames) narrative += " No hay un patrón recurrente de fallas por encima del 20%.";
  }

  return (
    <section className="panel agent-summary-panel" aria-labelledby="agent-summary-title">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">Lectura orientada al coaching</span>
          <h2 id="agent-summary-title">Resumen de {displayAgentName(agent)}</h2>
          <p className="panel-subcopy">Resumen general de todas las monitorias del período, con evidencia por criterio.</p>
        </div>
        <div className="agent-summary__actions">
          <span className="method-note">Basado en todas las monitorias disponibles</span>
          <ClearAgentButton agent={agent} onClear={onClear} />
        </div>
      </div>
      <p className="agent-summary__narrative">{narrative}</p>
      <div className="agent-summary__grid">
        <article className="agent-summary__column agent-summary__column--strength">
          <div className="agent-summary__column-heading">
            <span className="agent-summary__icon" aria-hidden="true">✓</span>
            <div><strong>Dónde es bueno</strong><small>Criterios sin fallas observadas</small></div>
          </div>
          <InsightList criteria={strengths} kind="strength" emptyMessage={eligibleCriteria.length ? "No hay criterios con 100% de aprobación en el período." : "No hay datos evaluables para señalar puntos fuertes."} />
        </article>
        <article className="agent-summary__column agent-summary__column--attention">
          <div className="agent-summary__column-heading">
            <span className="agent-summary__icon" aria-hidden="true">!</span>
            <div><strong>Dónde más falla</strong><small>Patrones recurrentes de fallo</small></div>
          </div>
          <InsightList criteria={attention} kind="attention" emptyMessage={eligibleCriteria.length ? "No hay un criterio con 20% o más de fallos." : "No hay datos evaluables para señalar fallas."} />
        </article>
        <article className="agent-summary__column agent-summary__column--rare">
          <div className="agent-summary__column-heading">
            <span className="agent-summary__icon" aria-hidden="true">≈</span>
            <div><strong>Desvíos raros</strong><small>Fallos puntuales, no recurrentes</small></div>
          </div>
          <InsightList criteria={rareDeviations} kind="rare" emptyMessage={eligibleCriteria.length ? "No hay desvíos raros entre 0% y 20% de fallos." : "No hay datos evaluables para identificar desvíos."} />
        </article>
      </div>
      <p className="agent-summary__footnote">La lectura usa todas las monitorias disponibles del agente en el período. 0% de fallos indica buen desempeño, más de 0% y menos de 20% indica un desvío raro, y 20% o más indica un patrón para coaching. Cada tarjeta muestra su propia muestra; no sustituye la revisión de la conversación.</p>
    </section>
  );
}

function MetricCard({ label, value, tone = "neutral" }: { label: string; value: string; tone?: string }) {
  return (
    <article className={`metric-card metric-card--${tone}`}>
      <span className="metric-card__signal" aria-hidden="true" />
      <p className="metric-card__label">{label}</p>
      <strong className="metric-card__value">{value}</strong>
    </article>
  );
}

function QualityHighlight({ label, value, detail, tone }: { label: string; value: string; detail: string; tone: "green" | "teal" | "blue" }) {
  return (
    <article className={`qa-highlight qa-highlight--${tone}`}>
      <p className="qa-highlight__label">{label}</p>
      <strong className="qa-highlight__value">{value}</strong>
      <span className="qa-highlight__detail">{detail}</span>
    </article>
  );
}

function AgentRow({ agent, rank, selected, onSelect }: { agent: QaAgentSummary; rank: number; selected: boolean; onSelect: () => void }) {
  const errorRate = incidenceRate(agent);

  return (
    <tr className={selected ? "is-selected" : undefined}>
      <td className="qa-rank" aria-label={`Posición ${rank}`}>{rank}</td>
      <th scope="row">
        <button className="agent-button" type="button" onClick={onSelect} aria-pressed={selected}>
          <span className="agent-avatar" aria-hidden="true">{agent.agent_name.slice(0, 1).toUpperCase()}</span>
          <span>
            <strong>{displayAgentName(agent.agent_name)}</strong>
            <small>{formatCount(agent.evaluations)} evaluaciones · {formatCount(agent.failed_criteria)} criterios fallidos</small>
          </span>
        </button>
      </th>
      <td className="qa-evaluations">{formatCount(agent.evaluations)}</td>
      <td className={qualityCellClass(agent.avg_score)}><strong>{formatScore(agent.avg_score)}</strong></td>
      <td className="qa-errors">{formatCount(agent.failed_evaluations)}</td>
      <td className={errorCellClass(errorRate)}><strong>{formatScore(errorRate)}</strong></td>
    </tr>
  );
}

function DrillPanel({ drill, agent }: { drill: QaDrillResponse | null; agent: string | null }) {
  if (!agent) {
    return (
      <div className="empty-panel empty-panel--compact">
        <span className="empty-panel__icon">↗</span>
        <div>
          <strong>Seleccione un agente para ver las evaluaciones</strong>
          <p>El agregado mensual permanece arriba; este panel muestra la evidencia de las llamadas seleccionadas.</p>
        </div>
      </div>
    );
  }
  if (!drill) return <div className="loading-block">Cargando detalle de {displayAgentName(agent)}…</div>;
  if (!drill.rows.length) return <div className="empty-panel empty-panel--compact"><strong>No hay evaluaciones para este agente.</strong></div>;

  return (
    <div className="drill-list">
      {drill.rows.map((row) => (
        <article className="drill-card" key={row.evaluation_id}>
          <div className="drill-card__topline">
            <div>
              <span className="eyebrow">Evaluación {new Date(row.evaluated_at).toLocaleDateString("es-UY")}</span>
              <code>{row.conversation_id}</code>
            </div>
            <span className={scoreClass(row.score_percentage)}>{formatScore(row.score_percentage)}</span>
          </div>
          {row.failed_criteria.length ? (
            <div className="failure-list">
              {row.failed_criteria.map((criterion) => (
                <span className="failure-chip" key={`${row.evaluation_id}-${criterion.criterion_key}`}>
                  {criterion.criterion_label_es || criterion.criterion_key}
                </span>
              ))}
            </div>
          ) : <span className="success-line">✓ Sin criterios fallidos</span>}
          {row.resumen_general_qa ? <p className="drill-card__summary">{row.resumen_general_qa}</p> : null}
        </article>
      ))}
    </div>
  );
}

export default function App() {
  const [months, setMonths] = useState<QaMonthsResponse | null>(null);
  const [month, setMonth] = useState("");
  const [agent, setAgent] = useState<string | null>(null);
  const [summary, setSummary] = useState<QaSummary | null>(null);
  const [drill, setDrill] = useState<QaDrillResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMonths()
      .then((result) => {
        setMonths(result);
        setMonth(result.latest_month || result.months[0]?.month || "");
      })
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "No se pudo cargar el período."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!month) return;
    setLoading(true);
    setError(null);
    setDrill(null);
    fetchSummary(month, agent)
      .then(setSummary)
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "No se pudo cargar el resumen."))
      .finally(() => setLoading(false));
  }, [month, agent]);

  useEffect(() => {
    if (!month || !agent) {
      setDrill(null);
      return;
    }
    setDetailLoading(true);
    fetchDrill(month, agent)
      .then(setDrill)
      .catch(() => setDrill(null))
      .finally(() => setDetailLoading(false));
  }, [month, agent]);

  const selectedMonthLabel = month ? formatMonth(month) : "Sin datos";
  const rankedAgents = useMemo(() => {
    if (!summary) return [];
    return [...summary.agents].sort((a, b) => {
      const scoreA = a.avg_score ?? -1;
      const scoreB = b.avg_score ?? -1;
      return scoreB - scoreA || b.evaluations - a.evaluations || a.agent_name.localeCompare(b.agent_name);
    });
  }, [summary]);
  const bestAgent = rankedAgents[0] ?? null;
  const lowestErrorAgent = [...rankedAgents].sort((a, b) => {
    const errorA = incidenceRate(a) ?? Number.POSITIVE_INFINITY;
    const errorB = incidenceRate(b) ?? Number.POSITIVE_INFINITY;
    return errorA - errorB || (b.avg_score ?? -1) - (a.avg_score ?? -1);
  })[0] ?? null;

  return (
    <main className="app-shell">
      <section className="brand-hero">
        <header className="brand-hero__header">
          <div className="brand-lockup">
            <div className="brand-logo-wrap">
              <img src={corefoneLogo} alt="Corefone" className="brand-logo" />
            </div>
            <div className="brand-copy">
              <span className="eyebrow">Corefone Operations</span>
              <strong>Tienda Inglesa</strong>
            </div>
          </div>
          <div className="freshness"><span className="status-dot" /> Snapshot operativo</div>
        </header>

        <div className="brand-hero__body">
          <div className="hero-copy hero-copy--report">
            <h1>Reporte mensual</h1>
          </div>
          <div className="brand-status-card">
            <div className="brand-status-card__heading">
              <span className="eyebrow">Período de lectura</span>
              <strong>{selectedMonthLabel}</strong>
            </div>
            <div className="filters" aria-label="Filtros del dashboard">
              <label>
                <span>Mes</span>
                <input type="month" value={month} onChange={(event) => { setMonth(event.target.value); setAgent(null); }} />
              </label>
              <label>
                <span>Agente</span>
                <select value={agent || ""} onChange={(event) => setAgent(event.target.value || null)}>
                  <option value="">Todos los agentes</option>
                  {summary?.available_agents.map((name) => <option key={name} value={name}>{displayAgentName(name)}</option>)}
                </select>
              </label>
            </div>
          </div>
        </div>
      </section>

      {error ? <div className="alert" role="alert">No se pudo cargar el dashboard: {error}</div> : null}

      {loading && !summary ? <div className="loading-block">Cargando el cierre mensual…</div> : null}

      {summary ? (
        <>
          <div className="section-heading">
            <div>
              <span className="eyebrow">Cierre del período</span>
              <h2>Resumen de calidad</h2>
            </div>
          </div>
          <section className="metric-grid" aria-label="Indicadores del período">
            <MetricCard label="Evaluaciones completas" value={formatCount(summary.totals.evaluations)} tone="green" />
            <MetricCard label="Score promedio" value={formatScore(summary.totals.avg_score)} tone="blue" />
            <MetricCard label="Sin incidencias" value={formatScore(summary.totals.clean_rate)} tone="amber" />
            <MetricCard label="Bajo 60%" value={formatCount(summary.totals.low_score_evaluations)} tone="red" />
          </section>

          <section className="content-grid content-grid--qa">
            <article className="panel panel--qa-agents">
              <div className="qa-report__titlebar">ANÁLISIS DE CALIDAD · AGENTES CONVERSACIONALES</div>
              <div className="qa-highlights">
                <QualityHighlight
                  label="MEJOR AGENTE"
                  value={bestAgent ? displayAgentName(bestAgent.agent_name) : "—"}
                  detail={bestAgent ? `${formatScore(bestAgent.avg_score)} · ${formatCount(bestAgent.evaluations)} evaluaciones` : "Sin datos"}
                  tone="green"
                />
                <QualityHighlight
                  label="MAYOR SCORE QA"
                  value={formatScore(bestAgent?.avg_score ?? null)}
                  detail={bestAgent ? displayAgentName(bestAgent.agent_name) : "Sin datos"}
                  tone="teal"
                />
                <QualityHighlight
                  label="MENOR TASA DE INCIDENCIA"
                  value={formatScore(incidenceRate(lowestErrorAgent))}
                  detail={lowestErrorAgent ? displayAgentName(lowestErrorAgent.agent_name) : "Sin datos"}
                  tone="blue"
                />
              </div>
              <div className="qa-section-bar">1. RESULTADOS GENERALES DE CALIDAD</div>
              {summary.agents.length ? (
                <div className="table-wrap">
                  <table className="qa-agent-table">
                    <thead><tr><th>Posición</th><th>Agente</th><th>Evaluaciones monitoreadas</th><th>Score QA</th><th>Incidencias</th><th>Tasa de incidencia</th></tr></thead>
                    <tbody>{rankedAgents.map((item, index) => <AgentRow key={item.agent_name} agent={item} rank={index + 1} selected={agent === item.agent_name} onSelect={() => setAgent(item.agent_name)} />)}</tbody>
                  </table>
                </div>
              ) : <div className="empty-panel"><strong>No hay evaluaciones en este período.</strong><p>Seleccione otro mes o importe el próximo resultado.</p></div>}
            </article>

            <article className="panel panel--criteria">
              <div className="panel-heading"><div><span className="eyebrow">Por qué</span><h2>Criterios con incidencia</h2><p className="panel-subcopy">Fallos sobre criterios efectivamente evaluados.</p></div></div>
              {summary.criteria.length ? <div className="criterion-list">
                {summary.criteria.slice(0, 8).map((criterion) => <div className="criterion-row" key={criterion.criterion_key}>
                  <div className="criterion-row__label"><strong>{criterion.criterion_label}</strong><small>{criterion.domain} · {criterion.failed_answers} fallas / {criterion.evaluated_answers} evaluadas</small></div>
                  <span className={criterion.failure_rate !== null && criterion.failure_rate >= 20 ? "rate rate--danger" : "rate"}>{formatScore(criterion.failure_rate)}</span>
                </div>)}
              </div> : <div className="empty-panel"><strong>Sin criterios evaluados.</strong></div>}
            </article>
          </section>

          {agent && summary.agent_filter === agent ? <AgentPerformanceSummary agent={agent} summary={summary} onClear={() => setAgent(null)} /> : null}

          <section className="panel panel--proof">
            <div className="panel-heading">
              <div><span className="eyebrow">Prueba de la lectura</span><h2>{agent ? `Evaluaciones de ${displayAgentName(agent)}` : "Detalle bajo demanda"}</h2></div>
              {agent ? <ClearAgentButton agent={agent} onClear={() => setAgent(null)} /> : null}
            </div>
            {detailLoading ? <div className="loading-block">Cargando evaluaciones…</div> : <DrillPanel drill={drill} agent={agent} />}
          </section>
        </>
      ) : null}

      {!loading && !summary && !error ? <div className="empty-panel empty-panel--page"><strong>No hay datos importados.</strong><p>Use el importador de Resultados.xlsx para alimentar la API.</p></div> : null}
    </main>
  );
}
